import useApiRequest, { ApiResponse } from "@/lib/hooks/use-request";
import { UserRole } from "@/lib/interfaces/user";
import {
  CreateInvitationRequestDto,
  InvitationCreationEntitiesResponseDto,
  UpdateUserRequestDto,
  UserDetailsResponseDto,
} from "@/lib/interfaces/users";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { DialogFooter } from "@/ui-components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

const UserEditor = ({
  item,
  onSubmitDone,
  withoutRedirect = false,
}: {
  item?: UserDetailsResponseDto;
  onSubmitDone?: () => void;
  withoutRedirect?: boolean;
}) => {
  const [creationEntities, setCreationEntities] = useState<InvitationCreationEntitiesResponseDto>();
  const [isLoading, setIsLoading] = useState(true);
  const { apiRequest } = useApiRequest();

  const getCreationEntities = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<InvitationCreationEntitiesResponseDto>(
      "settings/users/invitations/creation-entities",
      "GET"
    );
    setIsLoading(false);
    if (response.data) {
      setCreationEntities(response.data);
    }
  }, [apiRequest]);

  useEffect(() => {
    getCreationEntities();
  }, [getCreationEntities]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={creationEntities}
      renderElement={(items) => (
        <UserEditorForm
          item={item}
          creationEntities={items}
          onSubmitDone={onSubmitDone}
          withoutRedirect={withoutRedirect}
        />
      )}
    />
  );
};

const UserEditorForm = ({
  item,
  creationEntities,
  onSubmitDone,
  withoutRedirect,
}: {
  item?: UserDetailsResponseDto;
  creationEntities: InvitationCreationEntitiesResponseDto;
  onSubmitDone?: () => void;
  withoutRedirect?: boolean;
}) => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();

  const userSchema = z.object({
    firstName: z
      .string()
      .max(45, {
        message: "Vorname muss weniger als 45 Zeichen lang sein",
      })
      .min(1, {
        message: "Vorname ist erforderlich",
      }),
    lastName: z.string().min(1, {
      message: "Nachname ist erforderlich",
    }),
    mail: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
    role: z.nativeEnum(UserRole), // adjust based on your UserRole type
  });

  type UserFormSchema = z.infer<typeof userSchema>;

  const form = useForm<UserFormSchema>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      firstName: item?.firstName || "",
      lastName: item?.lastName || "",
      mail: item?.mail || "",
      role: item?.role || UserRole.User,
    },
  });

  const onSubmit = async (data: UserFormSchema) => {
    let res: ApiResponse<UserDetailsResponseDto>;
    if (item) {
      // Update existing user
      const req: UpdateUserRequestDto = {
        firstName: data.firstName !== item.firstName ? data.firstName : undefined,
        lastName: data.lastName !== item.lastName ? data.lastName : undefined,
        mail: data.mail !== item.mail ? data.mail : undefined,
        role: data.role !== item.role ? data.role : undefined,
      };

      res = await apiRequest<UserDetailsResponseDto>(`settings/users/${item._id}`, "PATCH", {
        body: req,
        toast: { toastText: "User erfolgreich aktualisiert" },
      });
    } else {
      // Create new user
      const req: CreateInvitationRequestDto = {
        firstName: data.firstName,
        lastName: data.lastName,
        mail: data.mail,
        role: data.role,
      };

      res = await apiRequest<UserDetailsResponseDto>("settings/users/invitations", "POST", {
        body: req,
        toast: { toastText: "User erfolgreich eingeladen" },
      });
    }
    if (res.data) {
      if (onSubmitDone) {
        onSubmitDone();
      }
      if (!withoutRedirect) {
        navigate(`/settings/users`);
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <FormLabel className="text-[16px] font-bold">User Name:*</FormLabel>
        <div className="flex gap-3">
          <div className="w-1/2">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg gap-[14px]">
                  <FormControl>
                    <Input {...field} placeholder="Vorname" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-1/2">
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg gap-[14px]">
                  <FormControl>
                    <Input {...field} placeholder="Nachname" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <FormLabel className="text-[16px] font-bold">Email:*</FormLabel>
        <FormField
          control={form.control}
          name="mail"
          render={({ field }) => (
            <FormItem className="flex flex-col bg-white rounded-lg gap-[14px]">
              <FormControl>
                <Input {...field} placeholder="Email" type="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormLabel className="text-[16px] font-bold">Rolle:*</FormLabel>
        {item && item.role === UserRole.SuperAdmin ? (
          <>Super Admin</>
        ) : (
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Rolle auswählen" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {creationEntities.roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <DialogFooter className="sm:justify-start">
          <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-full">
            <Mail /> <span>{item ? "User aktualisieren" : "Einladung senden"}</span>
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default UserEditor;
